import { useMemo, useState, type ComponentProps } from "react";
import { Megaphone } from "lucide-react";

import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button";
import { Dialog, DialogTrigger } from "@/modern/components/ui/dialog";
import { cn } from "@/modern/utils";

import { ChangelogDialogContent } from "./ChangelogDialog";
import { useChangelogSeen } from "./useChangelogSeen";

const CHANGELOG_BADGE_MAX = 99;

type ButtonVariant = ComponentProps<typeof Button>["variant"];
type ButtonSize = ComponentProps<typeof Button>["size"];

interface ChangelogBellProps {
  className?: string;
  /** Match surrounding chrome (e.g. mega menu footer uses ghost + sm). */
  variant?: ButtonVariant;
  size?: ButtonSize;
  onOpenChange?: (open: boolean) => void;
  /**
   * Header placement: omit the button once every update is read.
   * Stays mounted while the dialog is open so closing it can mark them read.
   */
  hideWhenRead?: boolean;
}

export function ChangelogBell({
  className,
  variant = "outline",
  size = "icon",
  onOpenChange,
  hideWhenRead = false,
}: ChangelogBellProps) {
  const {
    unreadCount,
    allVersions,
    seenEntryIds,
    allEntryIds,
    markSeen,
    markEntriesSeen,
  } = useChangelogSeen();
  const [open, setOpen] = useState(false);

  /** Live unseen ids — counters and New labels update as destinations are viewed. */
  const unseenEntryIds = useMemo(() => {
    const seen = new Set(seenEntryIds);
    return allEntryIds.filter((id) => !seen.has(id));
  }, [allEntryIds, seenEntryIds]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen === open) return;

    if (nextOpen) {
      setOpen(true);
      onOpenChange?.(true);
      return;
    }

    markSeen();
    setOpen(false);
    onOpenChange?.(false);
  };

  const isCompact = size === "sm" || size === "icon-sm";

  if (hideWhenRead && unreadCount === 0 && !open) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant={variant}
          size={size}
          className={cn(
            "relative shrink-0",
            !isCompact && "size-9 rounded-lg shadow-sm transition-shadow hover:shadow-md",
            isCompact && "px-2 shadow-none",
            className
          )}
          title="What's new"
          aria-label={
            unreadCount > 0 ? `${unreadCount} unread updates` : "What's new"
          }
        >
          <Megaphone className={isCompact ? "h-4 w-4" : "size-[1.125rem]"} />
          {unreadCount > 0 && (
            <Badge
              variant="default"
              aria-hidden
              className="pointer-events-none absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border-primary bg-primary px-1 text-[10px] font-medium text-primary-foreground"
            >
              {unreadCount > CHANGELOG_BADGE_MAX
                ? `${CHANGELOG_BADGE_MAX}+`
                : unreadCount}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <ChangelogDialogContent
        open={open}
        versions={allVersions}
        unseenEntryIds={unseenEntryIds}
        onViewEntries={markEntriesSeen}
        onClose={() => handleOpenChange(false)}
      />
    </Dialog>
  );
}
